import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Offcanvas, OffcanvasBody, Spinner } from "reactstrap"
import { fetchApplication } from "slices/campaigns/applications/thunk"
import { fetchInfluencerInsights, fetchInfluencerSocialAccount } from "slices/influencer/thunk"
import { AppDispatch, RootState } from "store"
import InfluencerDetails from "./InfluencerDetails"
import AudienceDetails from "./AudienceDetails"
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar'
import RequirementMatches from "./RequirementMatches"
import InfluencerProfile from "Components/influencer/InfluencerProfile"
import { ActionConfirmation } from "./ApplicationCard"
import { usePrivileges } from "hooks/useAuth"
import { PRIVILEGE } from "constants/privilege"
import CloseButton from "Components/Common/CloseButton"
import { ACCOUNT_NOT_FOUND_MSG } from "constants/global"
import { SocialAccount, SocialAccountDemographicInsights, SocialAccountInsights } from "types/influencer"
import { isEmpty } from "utils/helpers"
import { toggleApproveApplicationCostModal } from "slices/ui/reducer"

interface ApplicationOffCanvasProps {
    campaignId: string,
    isOpen: boolean,
    toggleOffcanvas: () => void,
    influencerId: string,
    socialAccountId?: string,
    approve?: () => void,
    reject?: () => void,
    // applicationStatus: 'pending' | 'accepted' | 'rejected' | 'processing',
}

const ApplicationOffCanvas: React.FC<ApplicationOffCanvasProps> = ({ campaignId, isOpen, toggleOffcanvas, influencerId, socialAccountId, approve, reject }) => {

    const [actionConfirmation, setActionConfirmation] = useState<null | ActionConfirmation>(null);
    const privileges = usePrivileges();
    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId]?.data?.campaign);


    const applicationData = useSelector((state: RootState) => state.campaignApplications.campaigns[campaignId]?.applications?.[influencerId]?.application) ?? null;
    const platform = applicationData?.influencer?.social_account?.platform;
    const applicationStatus = applicationData?.status;
    const socialAccount = useSelector((state: RootState) => state.influencers.social_accounts[socialAccountId!]?.social_account);
    const nicheList = useSelector((state: RootState) => state.auth.master?.niche_list ?? {});
    const connectionRequired = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign?.connection_required);
    const dispatch: AppDispatch = useDispatch();

    // const fetchInfluencerData = useCallback(async (influencerId: string, force: boolean = false) => {
    //     dispatch(fetchInfluencerInsights({ influencerId, force }));
    // }, [dispatch])


    useEffect(() => {
        if(isOpen && socialAccountId){
            dispatch(fetchInfluencerSocialAccount({socialAccountId}))
        }
    }, [isOpen, socialAccountId])
    

    const fetchApplicationData = useCallback(async (campaignId: string, influencerId: string, force: boolean = false) => {
        dispatch(fetchApplication({ campaignId, influencerId, force }));
    }, [dispatch])

    useEffect(() => {
        if (isOpen) {
            fetchApplicationData(campaignId, influencerId);
        }
    }, [influencerId, campaignId, isOpen])

    const getAudienceDetails = useCallback(() => {
        if (!socialAccount?.ig?.insights) return {};

        let audienceDetails: SocialAccountInsights = {};

        if(connectionRequired && socialAccount?.ig?.insights?.demographic && !isEmpty(socialAccount.ig?.insights?.demographic)){
            audienceDetails = {...socialAccount.ig?.insights?.demographic};
        }

        if(socialAccount?.ig?.insights?.niche_likes && !isEmpty(socialAccount.ig?.insights?.niche_likes)){
            const niche_likes: Record<string, any> = {};
            for(let key in socialAccount.ig.insights.niche_likes){
                const nicheName = nicheList[key];
                if(nicheName){
                    niche_likes[nicheName] = socialAccount.ig.insights.niche_likes[key];
                }
            }
            audienceDetails = {...audienceDetails, niche_likes};
        }

        return audienceDetails;
    }, [socialAccount]);


    return (
        <Offcanvas isOpen={isOpen} toggle={toggleOffcanvas} direction='end' id="influencer-offcanvas">
            <CloseButton onClose={toggleOffcanvas} />
            <OffcanvasBody className="vh-100 overflow-hidden influencer-offcanvas-body">
                <div className="d-flex justify-content-end gap-3">
                    <div className="info">
                        {
                            applicationData && socialAccount &&
                            <React.Fragment>
                                {
                                    (applicationData.influencer?.social_account) ? (
                                        <div className="section-1 position-relative">
                                            <PerfectScrollbar>
                                                <div>
                                                    <div>
                                                        <RequirementMatches
                                                            requirement_match={applicationData.requirement_match}
                                                            requirement_match_details={applicationData.requirement_match_details}
                                                            requirement_matches={applicationData.requirement_matches!}
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <InfluencerDetails application={applicationData} socialAccount={socialAccount} />
                                                    </div>
                                                    {
                                                        (socialAccount?.ig?.insights?.demographic && !isEmpty(socialAccount.ig?.insights?.demographic) || (socialAccount?.ig?.insights?.niche_likes || !isEmpty(socialAccount?.ig?.insights?.niche_likes))) &&
                                                        <div className="mt-3">
                                                            <AudienceDetails socialAccountInsights={getAudienceDetails()} />
                                                        </div>
                                                    }

                                                </div>

                                            </PerfectScrollbar>
                                            <div className="sticky-action">
                                                <div className="w-100 d-flex justify-content-center">
                                                    {
                                                        applicationStatus === 'pending' &&
                                                        <React.Fragment>
                                                            {
                                                                actionConfirmation ? (
                                                                    <div className={`confirmation-bar w-100 border-radius-6px theme-btn-lg d-flex justify-content-between align-items-center h-max-content px-3 ${actionConfirmation.action === 'reject' ? 'bg-danger' : 'bg-success'}`} onClick={e => e.stopPropagation()}>
                                                                        <span>Are you sure, You want to {actionConfirmation.action} ?</span>
                                                                        <div className="d-flex justify-content-end align-items-center gap-2">
                                                                            <button className="confirmation-btn" onClick={actionConfirmation.onConfirmation}>
                                                                                <i className='bx bx-check fs-4 text-success' />
                                                                            </button>
                                                                            <button className="confirmation-btn" onClick={e => setActionConfirmation(null)}>
                                                                                <i className='bx bx-x fs-4 text-danger' />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) :
                                                                    (approve && reject &&
                                                                        <div className="action-bar w-75">
                                                                            <div className="d-flex jusitfy-content-between align-items-center w-100">
                                                                                <button disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)} className="theme-btn reject btn-light-danger w-100 action-btn theme-btn-lg" onClick={() => setActionConfirmation({ action: 'reject', onConfirmation: reject })}><i className='bx bx-x me-1 fs-4' ></i>Reject</button>
                                                                                <button disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)} className="theme-btn approve btn-light-success w-100 action-btn theme-btn-lg" onClick={() => {
                                                                                    if(campaign?.has_transactions){
                                                                                        dispatch(toggleApproveApplicationCostModal({application: applicationData, isOpen: true}))
                                                                                    }else{
                                                                                        setActionConfirmation({ action: 'approve', onConfirmation: approve })
                                                                                    }
                                                                                }}><i className='bx bx-check me-1 fs-4' ></i>Approve</button>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                            }
                                                        </React.Fragment>
                                                    }
                                                    {
                                                        applicationStatus === 'processing' &&
                                                        <div className="confirmation-bar w-100 border-radius-6px theme-btn-lg d-flex justify-content-between align-items-center h-max-content px-3" onClick={e => e.stopPropagation()}>
                                                            <span>Processing...</span>
                                                            <Spinner size="sm" className='me-2'> Loading... </Spinner>
                                                        </div>
                                                    }
                                                    {
                                                        applicationStatus === 'rejected' &&
                                                        <div className="d-flex justify-content-center align-items-center h-max-content" onClick={e => e.stopPropagation()}>
                                                            <button className="theme-btn theme-btn-lg rejected-btn border-radius-6px">Rejected</button>
                                                        </div>
                                                    }
                                                    {
                                                        applicationStatus === 'accepted' &&
                                                        <div className="d-flex justify-content-center align-items-center h-max-content" onClick={e => e.stopPropagation()}>
                                                            <button className="theme-btn theme-btn-lg approved-btn border-radius-6px">Approved</button>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="account-not-available-card h-100">
                                            <span className="w-75">{ACCOUNT_NOT_FOUND_MSG}</span>
                                        </div>
                                    )
                                }

                            </React.Fragment>
                        }
                    </div>
                    <div className="influencer-content">
                        <div className="card mb-1">
                            {
                                applicationData &&
                                <div className="section-2" id="scrollableDiv">
                                    <InfluencerProfile influencer={applicationData.influencer} />
                                </div>
                            }
                            {
                                applicationData?.influencer?.social_account?.handle &&
                                <div className="sticky-action">
                                    <a href={
                                        `https://${platform === 'ig' ? 'instagram.com/' : 'youtube.com/@'}${applicationData.influencer.social_account.handle}`
                                        } target="_blank" rel="noopener noreferrer" className="theme-btn theme-btn-lg btn-gradient px-4 d-flex justify-content-between align-items-center w-75">
                                        Visit on {platform === 'ig' ? 'Instagram' : 'YouTube'} <i className='bx fs-4 bx-right-arrow-alt'></i>
                                    </a>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    )
}

export default ApplicationOffCanvas